import React, { useRef, useState } from "react";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import "./map.css";
import Map, {
  FullscreenControl,
  GeolocateControl,
  Layer,
  NavigationControl,
  Source,
} from "react-map-gl";

import { layerDefs } from "../layers/layerDefs";
import {
  visibleMapLayersWithPopupSelector,
  layerDefSourceDataSelector,
} from "../layers/recoil";

import ReferenceLayers  from '../layers/ReferenceLayers.jsx'
import LayersComponent from "../layers/LayersComponent.jsx";
import { Backdrop, CircularProgress } from "@mui/material";

import ExperianRetailLocationPopup from "../layers/hsds/experian/ExperianRetailLocationPopup.jsx"
import LoacGroupPopup from "../layers/hsds/loac/LoacGroupPopup.jsx"
import NonRetailPoiPopup from "../layers/hsds/non-retail-poi/NonRetailPoiPopup.jsx";
import VisitorAndSpendGridCellPopup from "../layers/hsds/visitor-and-spend/VisitorAndSpendGridCellPopup.jsx"

import TravelTimeSpinner from "../layers/hsds/travel-time/TravelTimeSpinner"
import { travelTimeFocusPointModeAtom } from "../layers/hsds/travel-time/atoms"

import { gis2TokenAtom } from "../login/loginAtoms";
import { tourMoveToStepSelector } from "../tour/tourAtom";
import { popupAtom } from "../popups/popupAtom";
import AttributePopup from "../popups/AttributePopup.jsx"
import { copyObject } from "../utils/types.js"

import {
  selectedFocusAreaAtom,
  selectedRegionSelector,
} from "./selectedRegionAtoms";
import { focusAreaLayerIdSelector } from "./focusAreaLayerAtoms.js"
import GeocoderControl from "./geocoder/GeocoderControl";

const OS_KEY = "vmRzM4mAA1Ag0hkjGh1fhA2hNLEM6PYP";

export default function HighstreetMap() {
  const mapRef = useRef();
  const [popup, setPopup] = useRecoilState(popupAtom);
  const visibleMapLayersWithPopup = useRecoilValue(
    visibleMapLayersWithPopupSelector
  );
  const layerIDs = Object.keys(layerDefs);
  const setTourStepIndex = useSetRecoilState(tourMoveToStepSelector);
  const selectedRegionState = useRecoilValue(selectedRegionSelector);
  const focusAreaLayerId = useRecoilValue(focusAreaLayerIdSelector);
  const focusAreaSourceURL = useRecoilValue(layerDefSourceDataSelector(focusAreaLayerId));
  const [selectedFocusArea, setSelectedFocusArea] = useRecoilState(selectedFocusAreaAtom);
  const [hoverFocusArea, setHoverFocusArea] = useState();
  const [mapLoading, setMapLoading] = useState(true);
  const gis2Token = useRecoilValue(gis2TokenAtom)
  const travelTimeFocusPointMode = useRecoilValue(travelTimeFocusPointModeAtom)
  const [focusAreaData, setFocusAreaData] = React.useState(null)

  React.useEffect(() => {
    setFocusAreaData(null)

    if (!focusAreaLayerId || !focusAreaSourceURL) {
      return
    }

    fetch(focusAreaSourceURL)
      .then((res) => res.json())
      .then((json) => {
        if (json.error) {
          throw new Error(json.error)
        }
        setFocusAreaData(json)
      })
      .catch((err) => {
        console.error(err)
      })
  }, [focusAreaSourceURL])

  const handleMapClick = (e) => {
    if (!mapRef?.current || !mapRef.current.loaded()) {
      return;
    }
    setPopup(null);

    const getFocusAreaFeatures = (map) => {
      if (!map.current.getLayer(focusAreaLayerId)) {
        return undefined
      }

      const features = map.current.queryRenderedFeatures(e.point, {
        layers: [focusAreaLayerId],
      })

      if (!features || features.length === 0 || !focusAreaData) {
        return undefined
      }

      const featurePart = features[0]
      return focusAreaData.features.find((f) => {
        return featurePart.properties.objectid === f.properties.objectid
      })
    }

    const selectFocusArea = (map) => {
      const feature = getFocusAreaFeatures(map)
      if (feature) {
        setSelectedFocusArea(copyObject(feature))
        setHoverFocusArea(null);
        setTourStepIndex(3);
      }
    }

    if (!selectedFocusArea) {
      selectFocusArea(mapRef)
      return;
    }

    if (travelTimeFocusPointMode) {
      return
    }

    showDetailsIfOverFeature(e, mapRef)
  };

  const showDetailsIfOverFeature = (e, map) => {
    if (visiblePopupLayers().length === 0) {
      return;
    }

    //console.log(map.current.getStyle().layers.filter((l) => !l.id.startsWith('OS/')))
    
    const features = map.current.queryRenderedFeatures(e.point, {
      layers: visiblePopupLayers(),
    });

    if (!features?.length) {
      return;
    }

    const mapLayerId = features[0]?.layer.id
    const layerDefId = findLayerDefIdFromPopupLayerId(mapLayerId)

    if (layerDefId) {
      showPopupViaFeature(e, layerDefId, layerDefs[layerDefId], features[0].properties)
    }
  }

  const visiblePopupLayers = () => {
    return Object.values(visibleMapLayersWithPopup).flat()
  }

  const findLayerDefIdFromPopupLayerId = (mapLayerId) => {
    for (const layerDefId in visibleMapLayersWithPopup) {
      if (visibleMapLayersWithPopup[layerDefId].includes(mapLayerId)) {
        return layerDefId
      }
    }
    return undefined
  }

  const showPopupViaFeature = (event, layerId, layer, properties) => {  
    if (layer.popupType) {
      showPopupWithType(event, layerId, properties, layer.popupType)
    } else {
      showPopupWithAttributes(event, layerId, properties, layer.popupAttributes)
    }
  }

  const showPopupWithType = (event, layerId, properties, type) => {
    setPopup({
      type: type,
      properties: properties,
      coords: event.lngLat,
      layerId: layerId,
    });
  }

  const showPopupWithAttributes = (event, layerId, properties, attributes, clickToSelect=false) => {
    setPopup({
      clickToSelect: clickToSelect,
      properties: properties,
      attributes: attributes,
      coords: event.lngLat,
      layerId: layerId,
    });
  }

  const handleMouseMove = (e) => {
    if (!mapRef?.current || selectedFocusArea) {
      return;
    }

    showDetailsIfOverHighStreet(e)
  };

  const showDetailsIfOverHighStreet = (e) => {
    mapRef.current.getCanvas().style.cursor = "";
    if (!mapRef.current.getLayer(focusAreaLayerId)) {
      return
    }

    const features = mapRef.current.queryRenderedFeatures(e.point, {
      layers: selectedFocusArea ? visiblePopupLayers() : [focusAreaLayerId],
    });

    if (!features.length || !features[0] || !focusAreaData) {
      setPopup(null);
      return;
    }

    const realFeature = focusAreaData.features.find((f) => {
      return f.properties.objectid === features[0].properties.objectid
    })

    showHighStreetDetailsPopup(e, realFeature, features[0].layer)
    mapRef.current.getCanvas().style.cursor = "pointer";
  }

  const showHighStreetDetailsPopup = (event, feature, layer) => {
    showPopupWithAttributes(
      event,
      layer.id,
      feature.properties,
      layerDefs[layer.id].popupAttributes,
      true,
    )
    setHoverFocusArea(feature.geometry);
  }

  const handleOnLoad = () => {
    setMapLoading(false);
  };

  const transformRequest = (url) => {
    if (url.toLowerCase().includes("//api.os.uk/")) {
      return transformOSRequest(url)
    }

    return {
      url: url
    }
  }

  const transformOSRequest = (url) => {
    const hasOSKey = (url) => /[?&]key=/.test(url)
    if (!hasOSKey(url)) {
      url += "?key=" + OS_KEY;
    }
    return {
      url: url + "&srs=3857",
    };
  }

  const transformGis2Request = (url) => {
    const result = {
      url: url
    }

    if (gis2Token) {
      result.url = `${result.url}&token=${gis2Token}`
    }

    return result
  }

  const renderAttributePopup = () => {
    if (!popup || popup.type || !popup.attributes || !popup.coords) {
      return undefined
    }
    return (<AttributePopup data={popup} />)
  }

  const renderExperianRetailLocationPopup = () => {
    if (!popup || popup.type !== "experian-retail-location") {
      return undefined
    }
    return (<ExperianRetailLocationPopup data={popup} />)
  }

  const renderVisitorAndSpendGridCellPopup = () => {
    if (!popup || popup.type !== "visitor-and-spend-grid-cell") {
      return undefined
    }
    return (<VisitorAndSpendGridCellPopup data={popup} />)
  }

  const renderLoacGroupPopup = () => {
    if (!popup || popup.type !== "loac-group") {
      return undefined
    }
    return (<LoacGroupPopup data={popup} />)
  }

  const renderNonRetailPoiPopup = () => {
    if (!popup || popup.type !== "non-retail-poi") {
      return undefined
    }
    return (<NonRetailPoiPopup data={popup}/>)
  }

  window.addEventListener("resize", () => {
    if (mapRef?.current) {
      setTimeout(() => {
        mapRef.current.resize();
      }, 200);
    }
  });

  return (
    <>
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={mapLoading || !focusAreaData}
      >
        <CircularProgress />
      </Backdrop>
      <div
        style={{ width: "100%", height: "100%" }}
        className="map-test-classname"
      >
        <Map
          preserveDrawingBuffer
          id="map"
          initialViewState={{
            longitude: 0.1545,
            latitude: 51.5151,
            zoom: 9.43,
            width: "100%",
            height: "100%",
            maxBounds: [
              [-0.9520147334466, 50.1004060804908],
              [2.03512883092415, 52.119059856816],
            ],
            minZoom: 9.2,
            transformRequest: transformRequest,
          }}
          onLoad={handleOnLoad}
          ref={mapRef}
          mapStyle={"./os_light_vts.json"}
          styleDiffing
          mapLib={maplibregl}
          style={{ width: "100%" }}
          hash={true}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          className="my-first-map"
        >
          <ReferenceLayers />
          {selectedRegionState && (
            <Source
              id="selected-region"
              type="geojson"
              data={selectedRegionState}
            >
              <Layer
                type="line"
                id="selected-region"
                paint={{
                  "line-color": "#353d42",
                  "line-dasharray": [1, 1],
                  "line-width": 2,
                }}
                beforeId="not-shown/ref-layer-to-insert-before/selected-region"
              ></Layer>
              <Layer
                type="fill"
                id="selected-region-fill"
                paint={{
                  "fill-color": "#353d42",
                  "fill-opacity": 0.05,
                }}
                beforeId="not-shown/ref-layer-to-insert-before/selected-region"
              ></Layer>
            </Source>
          )}
          {selectedFocusArea && (
            <Source
              id="selected-focus-area-region"
              type="geojson"
              data={selectedFocusArea.geometry}
            >
              <Layer
                type="line"
                id="selected-focus-area-region"
                paint={{
                  "line-color": "#353d42",
                  "line-width": 2,
                }}
                beforeId="not-shown/ref-layer-to-insert-before/selected-region"
              ></Layer>
              <Layer
                type="fill"
                id="selected-region-fill"
                paint={{
                  "fill-color": "#353d42",
                  "fill-opacity": 0.05,
                }}
                beforeId="not-shown/ref-layer-to-insert-before/base"
              ></Layer>
            </Source>
          )}
          {hoverFocusArea && (
            <Source id="hover-highstreet" type="geojson" data={hoverFocusArea}>
              <Layer
                type="line"
                id="hover-highstreet"
                paint={{
                  "line-color": "#353d42",
                  "line-width": 3,
                }}
              ></Layer>
              <Layer
                type="fill"
                id="selected-region-fill"
                paint={{
                  "fill-color": "#353d42",
                  "fill-opacity": 0.15,
                }}
              ></Layer>
            </Source>
          )}
          <LayersComponent layersIDs={layerIDs} />
          <TravelTimeSpinner />
          <GeocoderControl position="top-left" />
          <FullscreenControl position="top-left" />
          <GeolocateControl position="top-left" trackUserLocation />
          <NavigationControl position="top-left" />
          {renderAttributePopup()}
          {renderExperianRetailLocationPopup()}
          {renderVisitorAndSpendGridCellPopup()}
          {renderLoacGroupPopup()}
          {renderNonRetailPoiPopup()}
        </Map>
      </div>
    </>
  );
}
